import classNames from 'clsx';
import RBush from 'rbush';

import { commarize } from 'shared/common/utils/number';
import { BALLOON_HOVERED_Z_INDEX, BALLOON_Z_INDEX } from 'shared/map-search/constants/map';
import { EClustersType } from 'shared/map-search/types/clustersType';

import { getSpecialPromoMarkup } from './markups/specialPromo';
import * as styles from './styles.css';
import { formatBalloonCount, getIcon } from './utils';
import { getBuildingIcon } from './utils/getBuildingIcon';
import { IRBushItem, TFeaturePropertiesRequiredPrice, TFeature } from '../../../types/map';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
  // @todo выпилить эксп CD-159979
  showOnlyPrice: boolean;
  pointType: EClustersType | null;
}

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;
        public pointType: EClustersType | null;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        // @todo выпилить эксп CD-159979
        private showOnlyPrice: boolean;

        public constructor(options: IBalloonManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          // @todo выпилить эксп CD-159979
          this.showOnlyPrice = options.showOnlyPrice;
          this.objectManager = new ymaps.ObjectManager();
          this.pointType = options.pointType;
        }

        public updateBalloons = () => {
          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          const features = rBushItems
            .map(this.prepareBalloon)
            /**
             * @todo разработка в MNZ-700
             * Фильтруем пины, которые должны быть в промо-слое
             */
            .filter(feature => {
              if (!feature) {
                return false;
              }

              const isPulsed = feature.properties.isPulsedGlow;

              /**
               * @description
               * При редизайне показываем балуны либо если у них тип detailed, либо если внутри есть избранное
               */
              if (this.pointType) {
                return (
                  !isPulsed && (this.pointType === EClustersType.Detailed || !!feature.properties.favoriteIds?.length)
                );
              }

              return !isPulsed;
            }) as TFeature[];

          this.objectManager.add({
            type: 'FeatureCollection',
            features,
          });
        };

        public updateBalloonProperties = (featureId: string, properties: TFeaturePropertiesRequiredPrice) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
            zIndex: properties.isHovered ? BALLOON_HOVERED_Z_INDEX : BALLOON_Z_INDEX,
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (properties.type === 'peas') {
            return null;
          }

          if (!properties.minPrice && !(properties.specialPromo || properties.isNewbuildingMainPin)) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: BALLOON_Z_INDEX,
            },
          };
        };

        private prepareBalloonLayout = (properties: TFeaturePropertiesRequiredPrice) => {
          const content = this.prepareBalloonContent(properties);
          // @todo выпилить эксп CD-159979
          const showOnlyPrice = this.showOnlyPrice;
          const pointType = this.pointType;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;

              let shiftY = 6;

              if (!pointType) {
                shiftY = 4;
              } else {
                shiftY = pointType === EClustersType.OpenablePeas && properties.count > 1 ? 12 : 6;
              }

              let offsetY = -boundingRect.height - shiftY;

              // @todo выпилить эксп CD-159979
              if (showOnlyPrice) {
                offsetY += 12;
              }

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: TFeaturePropertiesRequiredPrice) {
          const {
            count,
            minPrice,
            favoriteIds,
            isViewed,
            hasNewobject,
            hasDiscountOffers,
            isActive,
            specialPromo,
            newbuilding,
            isAnyFromKp,
            clusterOfferIds,
            isGlow,
            isHovered,
            isNewbuildingMainPin,
          } = feature;

          // Если спец. проект (например, ПИК)
          if (specialPromo) {
            return getSpecialPromoMarkup(feature);
          }

          const isClustersRedesign = Boolean(this.pointType);
          const price = minPrice ? `${count > 1 ? 'от ' : ''}${commarize(minPrice)}` : '';
          const isFavorite = clusterOfferIds ? clusterOfferIds.some(id => favoriteIds?.includes(id)) : false;
          const countMarkup =
            count > 1
              ? `<div class="${classNames(styles['count'], {
                  [styles['promotion']]: isGlow && !isFavorite && !hasNewobject,
                })}">${formatBalloonCount(count, isClustersRedesign ? 99 : undefined)}</div>`
              : '';
          const iconMarkup = getIcon({
            isFavorite: newbuilding?.isFavorite || isFavorite,
            isPromo: hasDiscountOffers,
            isActive,
            isViewed,
            isRedesign: isClustersRedesign,
          });
          const newbuildingIcon = getBuildingIcon({
            isActive,
            isViewed,
          });
          const newbuildingId = newbuilding?.id ?? 0;
          const newbuildingText = newbuilding?.text ?? '';
          const isNewbuildingOnProPlus = !!newbuilding?.isAnyFicheringPlus || isAnyFromKp;

          let dataTestId = 'pin';
          if (isNewbuildingMainPin) {
            dataTestId = 'newbuilding-main-pin';
          } else if (isNewbuildingOnProPlus) {
            dataTestId = 'promo-pin';
          }

          return `
            <div
              data-testid="${dataTestId}"
              data-newbuilding="${newbuildingId}"
              class="${classNames({
                [styles['posutochno_wrapper']]: this.showOnlyPrice,
                [styles['wrapper']]: !this.showOnlyPrice,
                [styles['wrapper--redesign']]: isClustersRedesign,
                [styles['wrapper--cluster']]: this.pointType === EClustersType.OpenablePeas && count > 1,
                [styles['viewed']]: isViewed,
              })}
            ">
              <div class="${classNames(styles['balloon'], {
                [styles['residential-complex']]: hasNewobject || isAnyFromKp,
                [styles['newbuilding--main']]: newbuildingText,
                [styles['colorized']]: isNewbuildingOnProPlus,
                [styles['active']]: isActive,
                [styles['posutochno_balloon']]: this.showOnlyPrice,
                [styles['balloon--redesign']]: isClustersRedesign,
                [styles['balloon--hovered']]: isHovered && !isActive,
              })}
            ">
                ${isNewbuildingMainPin ? '' : countMarkup}
                ${isNewbuildingMainPin ? '' : `<div class="${styles['price']}">${price}</div>`}
                
                ${isNewbuildingMainPin ? newbuildingIcon : ''}
                ${isNewbuildingMainPin ? `<div class="${styles['price']}">${newbuildingText}</div>` : ''}

                <div class="${styles['icon']}">${iconMarkup}</div>
              </div>
            </div>
          `;
        }
      }

      provide(BalloonManager);
    },
  );
}
